import { convertISODateStrToBasicDateStr } from '.'
import { NewsDataRow, NewsTranslation } from './classes/graphql'
import {
  LinksDataRow,
  LinkTag,
  NewsContentType,
  TextsDataRow,
  TextTranslation,
  TransformedTranslationType,
  TranslationLang,
  UINewsTranslation
} from './types'

export const combineTranslations = (
  commonTextsJson: TextsDataRow[],
  clientTextsJson: TextsDataRow[]
): TextsDataRow[] => {
  const combinedJson: TextsDataRow[] = []

  commonTextsJson.forEach((commonDataRow) => {
    const clientDataRow = clientTextsJson.find(
      (clientDataRow) => clientDataRow.id === commonDataRow.id
    )
    if (clientDataRow) {
      const translations: TextTranslation[] = []
      commonDataRow.translations.forEach((commonTranslation) => {
        const clientTranslation = clientDataRow.translations.find(
          (clientTranslation) =>
            clientTranslation.lang === commonTranslation.lang
        )
        if (clientTranslation) {
          translations.push(clientTranslation)
        } else {
          translations.push(commonTranslation)
        }
      })

      combinedJson.push({ ...commonDataRow, translations })
    } else {
      combinedJson.push(commonDataRow)
    }
  })

  return combinedJson
}

const getLinkTranslation = (
  lang: TranslationLang,
  linksJson: LinksDataRow[],
  tag: LinkTag,
  labelKey: string,
  urlKey: string
) => {
  const linksDataRow: LinksDataRow | undefined = linksJson.find(
    (linksRow) => linksRow.tag === tag
  )

  if (linksDataRow) {
    const linkValue = linksDataRow.translations.find(
      (translation) => translation.lang === lang
    )
    if (linkValue) {
      return {
        [labelKey]: linkValue.label,
        [urlKey]: linkValue.value
      }
    }
  }
  return undefined
}

export const getTextTranslationsForLang = (
  lang: TranslationLang,
  combinedTranslationsJson: TextsDataRow[],
  linksJson: LinksDataRow[]
) => {
  let translations: Record<string, string> = {}

  combinedTranslationsJson.forEach((combinedRow) => {
    const key = combinedRow.id
    const commonTranslation = combinedRow.translations.find(
      (translation) => translation.lang === lang
    )
    if (commonTranslation) {
      translations = {
        ...translations,
        [key]: commonTranslation.text
      }
    }
  })

  translations = {
    ...translations,
    ...getLinkTranslation(
      lang,
      linksJson,
      'frontPageFooterContact',
      'contact.link.label',
      'contact.link.url'
    ),
    ...getLinkTranslation(
      lang,
      linksJson,
      'frontPageFooterTerms',
      'terms.link.label',
      'terms.link.url'
    )
  }

  return translations
}

export const getTranslationsForLang = <
  T extends { lang: string },
  L extends { translations: T[] },
  S extends TransformedTranslationType<T>
>(
  translationLang: TranslationLang,
  translationsDataRows: L[],
  translationTransformerFn?: (
    translationsDataRow: L,
    translation: TransformedTranslationType<T>
  ) => S
): S[] => {
  const translationsForLang = translationsDataRows.reduce<S[]>(
    (accumulator, currentItem) => {
      const langTranslation = currentItem.translations.find(
        (translation) => translation.lang === translationLang
      )

      if (langTranslation) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { lang, ...uiTranslation } = langTranslation

        const transformedTranslation = translationTransformerFn
          ? translationTransformerFn(currentItem, uiTranslation)
          : (uiTranslation as S)

        accumulator.push(transformedTranslation)
      }
      return accumulator
    },
    []
  )

  return translationsForLang
}

export const getFrontPageLinkTranslationsForLang = (
  lang: TranslationLang,
  linksDataRows: LinksDataRow[]
) => {
  const frontPageLinks: LinksDataRow[] = linksDataRows.filter(
    (linksRow) => linksRow.tag === 'frontPage'
  )

  const frontPageLinksForLang = getTranslationsForLang(lang, frontPageLinks)

  return frontPageLinksForLang
}

const transformToUINewsTranslation = (
  translationsDataRow: NewsDataRow,
  translation: TransformedTranslationType<NewsTranslation>
): UINewsTranslation => ({
  ...translation,
  formattedStartDate: convertISODateStrToBasicDateStr(translation.startDate),
  type: translationsDataRow.type as NewsContentType
})

export const getNewsTranslationsForLang = (
  lang: TranslationLang,
  newsDataRows: NewsDataRow[]
) => {
  return getTranslationsForLang<
    NewsTranslation,
    NewsDataRow,
    UINewsTranslation
  >(lang, newsDataRows, transformToUINewsTranslation)
}
